import { Injectable, inject } from '@angular/core';
import { classes } from 'polytype';
import type { Observable } from 'rxjs';

import type {
    ProjectAssetCoverage,
    ProjectEvidenceSnapshot,
    ProjectFeedbackImproveLoop,
    ProjectLifecycleStage,
} from '@app/modules/project-home-page/project-home-page.component';
import { PROJECT_PATH } from '@app/shared/constants/api-route.config';
import type { ProjectAdapter, ProjectMemberRoleAdapter } from '@app/shared/models/project.model';
import type { ItemResponse, ListResponse } from '@app/shared/services/mixins/crud.service';
import { WithCrudService } from '@app/shared/services/mixins/crud.service';
import { RestService } from '@app/shared/services/rest.service';

@Injectable({
    providedIn: 'root',
})
export class ProjectService extends classes(WithCrudService<ProjectAdapter>) {
    protected endpoint = PROJECT_PATH;

    protected readonly api = inject(RestService);

    getBySlug(slug: string, showError = true): Observable<ItemResponse<ProjectAdapter>> {
        return this.api.get<ItemResponse<ProjectAdapter>>(
            `${this.endpoint}slug/${slug}`,
            showError,
        );
    }

    getLifecycle(
        slug: string,
        showError = true,
    ): Observable<ItemResponse<ProjectLifecycleStage[]>> {
        return this.api.get<ItemResponse<ProjectLifecycleStage[]>>(
            `${this.endpoint}slug/${slug}/lifecycle`,
            showError,
        );
    }

    getEvidenceSnapshot(
        slug: string,
        showError = true,
    ): Observable<ItemResponse<ProjectEvidenceSnapshot[]>> {
        return this.api.get<ItemResponse<ProjectEvidenceSnapshot[]>>(
            `${this.endpoint}slug/${slug}/evidence_snapshot`,
            showError,
        );
    }

    getAssetCoverage(
        slug: string,
        showError = true,
    ): Observable<ItemResponse<ProjectAssetCoverage[]>> {
        return this.api.get<ItemResponse<ProjectAssetCoverage[]>>(
            `${this.endpoint}slug/${slug}/asset_coverage`,
            showError,
        );
    }

    getFeedbackImproveLoop(
        slug: string,
        showError = true,
    ): Observable<ItemResponse<ProjectFeedbackImproveLoop>> {
        return this.api.get<ItemResponse<ProjectFeedbackImproveLoop>>(
            `${this.endpoint}slug/${slug}/feedback_improve_loop`,
            showError,
        );
    }

    updateGeneralAccess(
        body: { projectSlug: string; generalAccessRole: string | null },
        showError = true,
    ): Observable<ItemResponse<ProjectAdapter>> {
        return this.api.patch<ItemResponse<ProjectAdapter>>(
            `${this.endpoint}general_access`,
            body,
            showError,
        );
    }

    listMemberRoles(projectSlug: string): Observable<ListResponse<ProjectMemberRoleAdapter>> {
        return this.api.getWithParams<ListResponse<ProjectMemberRoleAdapter>>(
            `${this.endpoint}member_roles`,
            { projectSlug },
        );
    }

    updateProjectMemberRoles(body: {
        projectSlug: string;
        userId?: number | null;
        teamId?: number | null;
        projectRoleIds: number[];
    }): Observable<ListResponse<ProjectMemberRoleAdapter>> {
        return this.api.patch<ListResponse<ProjectMemberRoleAdapter>>(
            `${this.endpoint}member_roles`,
            body,
        );
    }

    requestProjectAccess(body: {
        projectSlug: string;
        projectRoleIds: number[];
    }): Observable<ListResponse<ProjectMemberRoleAdapter>> {
        return this.api.post<ListResponse<ProjectMemberRoleAdapter>>(
            `${this.endpoint}member_roles/request_access`,
            body,
        );
    }

    approveProjectAccess(body: {
        projectMemberRoleIds: number[];
    }): Observable<ListResponse<ProjectMemberRoleAdapter>> {
        return this.api.post<ListResponse<ProjectMemberRoleAdapter>>(
            `${this.endpoint}member_roles/approve_access`,
            body,
        );
    }

    rejectProjectAccess(body: {
        projectMemberRoleIds: number[];
    }): Observable<ItemResponse<{ status: string }>> {
        return this.api.post<ItemResponse<{ status: string }>>(
            `${this.endpoint}member_roles/reject_access`,
            body,
        );
    }
}
